export function TermsLayout() {
	return (
		<div className="mx-auto max-w-3xl p-4 pb-16">
			<div className="mb-8">
				<h1 className="text-title1 text-foreground">
					Terms of Service
				</h1>
				<p className="mt-2 text-body2 text-muted-foreground">
					<strong>Tenbagger (tenbagger.xyz)</strong>
				</p>
				<p className="mt-1 text-body2 italic text-muted-foreground">
					Last Updated: April 14, 2026
				</p>
			</div>

			<hr className="mb-6 border-border" />

			<div className="flex flex-col gap-8 text-body2 text-foreground">
				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						1. Acceptance of Terms
					</h2>
					<p className="text-muted-foreground">
						By accessing or using the Tenbagger platform ("Platform"), available
						at tenbagger.xyz, you ("User") agree to be bound by these Terms of
						Service ("Terms"). If you do not agree to all of these Terms, you
						must immediately cease use of the Platform.
					</p>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						2. Description of Service
					</h2>
					<p className="mb-3 text-muted-foreground">
						Tenbagger is a <strong>front-end user interface</strong> that
						provides a trading terminal experience. The Platform itself does
						not operate an exchange, hold custody of assets, execute trades, or
						manage user funds.
					</p>
					<ul className="list-disc space-y-2 pl-6 text-muted-foreground">
						<li>
							<strong>Trading Infrastructure & Order Execution</strong>: All
							trading functionality, order matching, settlement, market data,
							and related backend operations are provided by and executed on{" "}
							<strong>Hyperliquid</strong>, a third-party decentralized
							exchange protocol. Tenbagger has no control over, and assumes no
							responsibility for, the operation, availability, accuracy, or
							security of Hyperliquid's infrastructure.
						</li>
						<li>
							<strong>Account Authentication</strong>: User account creation
							and authentication are operated by <strong>Tenbagger</strong>{" "}
							using email-based one-time verification codes. Digital asset
							wallets associated with user accounts are created and maintained
							within Tenbagger's own secure infrastructure; private keys are
							stored in encrypted form and are never exposed through the user
							interface.
						</li>
					</ul>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						3. Nature of the Platform — Front-End Interface Only
					</h2>
					<p className="mb-3 text-muted-foreground">
						The User expressly acknowledges and agrees that:
					</p>
					<div className="space-y-3 text-muted-foreground">
						<p>
							(a) Tenbagger is solely a{" "}
							<strong>graphical user interface (GUI)</strong> that displays
							information from, and transmits user instructions to, third-party
							protocols and services.
						</p>
						<p>
							(b) Tenbagger does <strong>not</strong> operate as a broker,
							dealer, exchange, custodian, investment adviser, or financial
							institution of any kind.
						</p>
						<p>
							(c) Tenbagger does <strong>not</strong> have custody, possession,
							or control of any User funds, digital assets, private keys, or
							wallets at any time.
						</p>
						<p>
							(d) All trades, positions, liquidations, and financial
							transactions are executed entirely on the Hyperliquid protocol.
							Tenbagger merely relays User instructions to Hyperliquid and
							displays data received from Hyperliquid.
						</p>
						<p>
							(e) Tenbagger does <strong>not</strong> guarantee the accuracy,
							completeness, or timeliness of any data displayed on the
							Platform, including but not limited to prices, balances, order
							statuses, funding rates, or P&L figures.
						</p>
					</div>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						4. Assumption of Risk
					</h2>
					<p className="mb-3 text-muted-foreground">
						By using the Platform, the User acknowledges and accepts the
						following risks:
					</p>
					<div className="space-y-3 text-muted-foreground">
						<p>
							(a) <strong>Trading Risk</strong>: Trading digital assets and
							derivatives involves substantial risk of loss. The User may lose
							all deposited funds. Past performance is not indicative of future
							results.
						</p>
						<p>
							(b) <strong>Protocol Risk</strong>: Hyperliquid, as a
							decentralized protocol, is subject to smart contract
							vulnerabilities, bugs, exploits, network congestion, downtime,
							and other technical failures. Tenbagger bears no responsibility
							for any losses arising from such events.
						</p>
						<p>
							(c) <strong>Liquidation Risk</strong>: Positions may be
							liquidated automatically by the Hyperliquid protocol. Tenbagger
							has no ability to prevent, delay, or reverse liquidations.
						</p>
						<p>
							(d) <strong>Technology Risk</strong>: The Platform may experience
							downtime, latency, display errors, or other malfunctions. The
							User acknowledges that such events may result in missed trades,
							incorrect order submissions, or other adverse outcomes.
						</p>
						<p>
							(e) <strong>Authentication Risk</strong>: Account access is
							granted through the User's email address. Compromise of the
							User's email account may permit unauthorized access to the
							User's Tenbagger account. The User is solely responsible for
							safeguarding access to their email account and any verification
							codes received.
						</p>
						<p>
							(f) <strong>Regulatory Risk</strong>: The legal status of digital
							asset trading varies by jurisdiction and is subject to change.
							The User is solely responsible for determining the legality of
							their use of the Platform in their jurisdiction.
						</p>
					</div>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						5. Disclaimer of Warranties
					</h2>
					<p className="mb-3 text-muted-foreground">
						THE PLATFORM IS PROVIDED ON AN <strong>"AS IS"</strong> AND{" "}
						<strong>"AS AVAILABLE"</strong> BASIS WITHOUT WARRANTIES OF ANY
						KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT
						LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
						PURPOSE, TITLE, AND NON-INFRINGEMENT.
					</p>
					<p className="mb-3 text-muted-foreground">
						TENBAGGER DOES NOT WARRANT THAT:
					</p>
					<ul className="list-disc space-y-2 pl-6 text-muted-foreground">
						<li>
							THE PLATFORM WILL BE UNINTERRUPTED, TIMELY, SECURE, OR
							ERROR-FREE
						</li>
						<li>
							THE INFORMATION, DATA, OR CONTENT DISPLAYED WILL BE ACCURATE,
							RELIABLE, OR COMPLETE;
						</li>
						<li>ANY DEFECTS OR ERRORS WILL BE CORRECTED;</li>
						<li>
							THE PLATFORM WILL BE COMPATIBLE WITH ANY THIRD-PARTY SOFTWARE,
							HARDWARE, OR SERVICES.
						</li>
					</ul>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						6. Limitation of Liability
					</h2>
					<div className="space-y-3 text-muted-foreground">
						<p>
							TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, TENBAGGER, ITS
							OPERATORS, FOUNDERS, EMPLOYEES, AFFILIATES, AGENTS, AND SERVICE
							PROVIDERS SHALL NOT BE LIABLE FOR ANY:
						</p>
						<p>
							(a) DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
							EXEMPLARY, OR PUNITIVE DAMAGES;
						</p>
						<p>
							(b) LOSS OF PROFITS, REVENUE, DATA, GOODWILL, OR DIGITAL ASSETS;
						</p>
						<p>
							(c) DAMAGES ARISING FROM: (i) USE OF OR INABILITY TO USE THE
							PLATFORM; (ii) ANY CONDUCT OR CONTENT OF THIRD PARTIES, INCLUDING
							HYPERLIQUID; (iii) UNAUTHORIZED ACCESS TO OR ALTERATION
							OF USER DATA OR TRANSMISSIONS; (iv) ERRORS, INACCURACIES, OR
							OMISSIONS IN DISPLAYED DATA; (v) DELAYED, FAILED, OR INCORRECTLY
							EXECUTED TRADES; (vi) LIQUIDATIONS OR MARGIN CALLS; (vii) SMART
							CONTRACT FAILURES OR EXPLOITS ON HYPERLIQUID; (viii) ANY OTHER
							MATTER RELATING TO THE PLATFORM;
						</p>
						<p>
							REGARDLESS OF THE THEORY OF LIABILITY (CONTRACT, TORT,
							NEGLIGENCE, STRICT LIABILITY, OR OTHERWISE), EVEN IF TENBAGGER
							HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
						</p>
						<p>
							IN NO EVENT SHALL TENBAGGER'S AGGREGATE LIABILITY EXCEED THE
							LESSER OF (A) THE FEES ACTUALLY PAID BY THE USER TO TENBAGGER IN
							THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED
							U.S. DOLLARS (USD $100).
						</p>
					</div>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						7. Indemnification
					</h2>
					<p className="mb-3 text-muted-foreground">
						The User agrees to indemnify, defend, and hold harmless Tenbagger,
						its operators, founders, employees, affiliates, and agents from and
						against any and all claims, damages, obligations, losses,
						liabilities, costs, and expenses (including reasonable attorneys'
						fees) arising from:
					</p>
					<div className="space-y-3 text-muted-foreground">
						<p>(a) The User's access to or use of the Platform;</p>
						<p>(b) The User's violation of these Terms;</p>
						<p>
							(c) The User's violation of any applicable law or regulation;
						</p>
						<p>
							(d) The User's trading activities, including any losses incurred;
						</p>
						<p>
							(e) Any dispute between the User and any third party, including
							Hyperliquid.
						</p>
					</div>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						8. Third-Party Services
					</h2>
					<p className="mb-3 text-muted-foreground">
						The Platform integrates with and relies upon third-party services,
						including but not limited to Hyperliquid. The User
						acknowledges that:
					</p>
					<div className="space-y-3 text-muted-foreground">
						<p>
							(a) Tenbagger is not a party to, and has no control over, any
							agreement between the User and any third-party service provider.
						</p>
						<p>
							(b) The User's use of third-party services is subject to the
							respective terms of service, privacy policies, and other
							agreements of those third parties.
						</p>
						<p>
							(c) Tenbagger makes no representations or warranties regarding
							the performance, security, or legality of any third-party
							service.
						</p>
						<p>
							(d) Tenbagger shall not be liable for any act, omission, failure,
							or breach by any third-party service provider.
						</p>
					</div>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						9. No Financial Advice
					</h2>
					<p className="text-muted-foreground">
						Nothing on the Platform constitutes financial, investment, legal,
						or tax advice. The User should consult qualified professionals
						before making any trading or investment decisions. All trading
						decisions are made solely by the User at their own risk.
					</p>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						10. Prohibited Use
					</h2>
					<p className="mb-3 text-muted-foreground">The User agrees not to:</p>
					<div className="space-y-3 text-muted-foreground">
						<p>
							(a) Use the Platform in violation of any applicable law,
							regulation, or sanction;
						</p>
						<p>
							(b) Use the Platform if they are located in, or a citizen or
							resident of, any jurisdiction where such use would be prohibited;
						</p>
						<p>
							(c) Attempt to interfere with, disrupt, or compromise the
							integrity or security of the Platform;
						</p>
						<p>
							(d) Use the Platform for any form of market manipulation, fraud,
							or illicit activity
						</p>
						<p>
							(e) Reverse-engineer, decompile, or disassemble any part of the
							Platform.
						</p>
					</div>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						11. Modification of Terms
					</h2>
					<p className="text-muted-foreground">
						Tenbagger reserves the right to modify these Terms at any time.
						Changes will be effective upon posting to the Platform. Continued
						use of the Platform after any modification constitutes acceptance
						of the revised Terms. It is the User's responsibility to review
						these Terms periodically.
					</p>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						12. Termination
					</h2>
					<p className="text-muted-foreground">
						Tenbagger may, at its sole discretion and without prior notice,
						suspend or terminate User access to the Platform for any reason,
						including but not limited to suspected violation of these Terms.
						Upon termination, the User remains responsible for any outstanding
						obligations.
					</p>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						13. Governing Law & Dispute Resolution
					</h2>
					<div className="space-y-3 text-muted-foreground">
						<p>
							These Terms shall be governed by and construed in accordance with
							the laws of{" "}
							<strong>Saint Vincent and the Grenadines</strong>, without regard
							to its conflict of law principles.
						</p>
						<p>
							Any dispute, controversy, or claim arising out of or relating to
							these Terms, or the breach, termination, or invalidity thereof,
							shall be settled by binding arbitration administered in
							accordance with the{" "}
							<strong>
								Arbitration Rules of the International Chamber of Commerce
								(ICC)
							</strong>
							. The seat of arbitration shall be{" "}
							<strong>Kingstown, Saint Vincent and the Grenadines</strong>. The
							arbitration shall be conducted in English by a single arbitrator.
							The arbitrator's decision shall be final and binding, and
							judgment on the award may be entered in any court of competent
							jurisdiction.
						</p>
						<p>
							The User agrees that any arbitration shall be conducted on an
							individual basis only.{" "}
							<strong>
								The User waives any right to participate in class action
								lawsuits, class-wide arbitration, or any form of representative
								or consolidated proceedings.
							</strong>
						</p>
						<p>
							The User further agrees that any claim must be brought within{" "}
							<strong>one (1) year</strong> after the cause of action arises,
							or such claim shall be permanently barred.
						</p>
					</div>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						14. Severability
					</h2>
					<p className="text-muted-foreground">
						If any provision of these Terms is found to be unenforceable or
						invalid, that provision shall be limited or eliminated to the
						minimum extent necessary, and the remaining provisions shall remain
						in full force and effect.
					</p>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						15. Entire Agreement
					</h2>
					<p className="text-muted-foreground">
						These Terms constitute the entire agreement between the User and
						Tenbagger regarding the use of the Platform and supersede all prior
						agreements or understandings.
					</p>
				</section>

				<section>
					<h2 className="mb-3 text-title2 text-foreground">
						16. Contact
					</h2>
					<p className="text-muted-foreground">
						For questions regarding these Terms, please contact:{" "}
						<a
							href="mailto:contact@tenbagger.xyz"
							className="text-foreground underline underline-offset-2 hover:text-muted-foreground"
						>
							contact@tenbagger.xyz
						</a>
					</p>
				</section>

				<hr className="border-border" />

				<p className="italic text-muted-foreground">
					By using Tenbagger, you confirm that you have read, understood, and
					agree to be bound by these Terms of Service.
				</p>
			</div>
		</div>
	);
}
